import { useMemo } from "react";
import { getIn, useFormikContext } from "formik"
import {
  FormikDateSelector,
  FormikFileUpload,
  FormikSelect,
  FormikNotesArea
} from "../../../../../../components/widgets"

import {
  DLMGenerateDOCButton,
  DLMGenerateROIButton
} from "../components";
import { useDLMResponseFormContext } from "../../components";

import { useUserLoader } from "../../../../../core/users";

import * as Yup from 'yup'

import {
  DOCModelsSchema,
  IDLMWebDOCModel,
  IDLMWebROIModel,
  ROIModelsSchema,
} from "../../../../../core/doclabel";

import { DLMCreateROIButton } from "../../../widgets/DLMCreateROIButton";
import { DLMCreateDOCButton } from "../../../widgets/DLMCreateDOCButton";
import { IWebFileAttachment } from "../../../../../core/applications";
import { filter, sortBy } from "lodash";

// Admin Review
export interface IDLMAdminReview  {
  verdict: string
  appType: string
  note: string
  noteAttachments: Array<IWebFileAttachment>    
  inspectionDate: string
  inspectorID: number
  pmID: number
  qaId: number
  docModels: Array<IDLMWebDOCModel>
  roiModels: Array<IDLMWebROIModel> 
}

export const initDLMAdminReviewValues = {
  verdict: '',
  appType: '',
  note: '',
  noteAttachments: [],  
  inspectionDate: '',
  inspectorID: 0,
  pmID: 0,
  qaId: 0,
  docModels:[],
  roiModels:[],    
}

export const DLMAdminReviewSchema: any = Yup.object({
  verdict: Yup.string().trim(),
  note: Yup.string().trim().label('Note')
  .when('verdict', {
    is: (val: string) => (val === 'rejected'),
    then: (schema) => schema.required(),
  }),
  noteAttachments: Yup.array(),

  inspectionDate: Yup.string().trim().label('Inspection Date')
  .when(['workflow', 'verdict'], {
    is : (workflow: string, verdict: string) => (verdict === 'approved' && ['SiteInspect','LabelInspect','MandatoryDOC','MandatoryDOC1B','Label1b1','Label1b2','LabelReplacement'].includes(workflow)),
    then: (schema) => schema.required(),
  }),
  inspectorID: Yup.number().integer().label('Inspector')
  .when(['workflow', 'verdict'], {
    is : (workflow: string, verdict: string) => (verdict === 'approved' && ['SiteInspect','LabelInspect','MandatoryDOC','MandatoryDOC1B','Label1b1','Label1b2','LabelReplacement'].includes(workflow)),
    then: (schema) => schema.required().moreThan(0,'Select Site Inspector'),
  }),
  pmID: Yup.number().integer().label('Program Manager')
  .when(['workflow', 'verdict'], {
    is : (workflow: string, verdict: string) => (verdict === 'approved' && ['DOC','Label5','DOCEdit','DOCSplit','ROIEdit','ROISplit'].includes(workflow)),
    then: (schema) => schema.required().moreThan(0,'Select Program Manager'),
  }),
  qaId: Yup.number().integer().label('Quotation Admin')
  .when(['workflow', 'verdict'], {
    is : (workflow: string, verdict: string) => (verdict === 'approved' && ['TriggerTest'].includes(workflow)),
    then: (schema) => schema.required().moreThan(0,'Select Quotation Admin'),
  }),
  docModels: Yup.array().label("DOC Attachments")
  .when(['workflow', 'verdict'], {
    is : (workflow: string, verdict: string) => (verdict === 'approved' && ['DOC','DOCEdit','DOCSplit'].includes(workflow)),
    then: (schema) => schema.required().of(DOCModelsSchema).min(1, 'DOC Required')
  }),
  roiModels: Yup.array().label("ROI Attachments")
  .when(['workflow', 'verdict'], {
    is : (workflow: string, verdict: string) => (verdict === 'approved' && ['Label5','Label1b1','ROISplit','ROIEdit','LabelReplacement'].includes(workflow)),
    then: (schema) => schema.of(ROIModelsSchema).required().min(1, 'ROI Required').label("ROI Attachment")
  })    
});

export const DLMAdminReviewForm = () => {

  const { appID, siteID } = useDLMResponseFormContext();

  const { values, setFieldValue, validateField } = useFormikContext();

  const { verdict, workflow, docModels, roiModels } = useMemo(() => {
    const verdict = getIn(values, 'verdict');
    const workflow = getIn(values, 'workflow');
    const docModels = getIn(values, 'docModels');
    const roiModels = getIn(values, 'roiModels');
    return { verdict, workflow, docModels: filter(docModels ?? [], (o: any) => (o?.cbRef)),  roiModels}

  }, [values]);

  const { data: inspectors } = useUserLoader({userType: (['SiteInspect','LabelInspect','MandatoryDOC','MandatoryDOC1B','Label1b1','Label1b2','LabelReplacement'].includes(workflow)) ? 'DLM Inspector' : ''});    
  const { data: programManagers } = useUserLoader({userType: (['DOC','Label5','DOCEdit','DOCSplit','ROIEdit','ROISplit'].includes(workflow)) ? "DLM Program Manager" : ''});
  const { data: quotationAdmins } = useUserLoader({userType: (['TriggerTest'].includes(workflow)) ? "CAMS Quotation Admin" : ''});

  const setDOCModel = (values: any, idx: number) => {
    if (! (values && typeof(values) === 'object' && values?.docId)) return;

    // clear attachment
    Object.assign(values, {
      docAttachmentId: 0,
      docAttachmentIdAttachmentName: ''
    });

    setFieldValue(`docModels.${idx}`,values, true);
  }

  const setROIModel = (values: any, idx: number) => {
    if (! (values && typeof(values) === 'object' && values?.roiId)) return;

    // clear attachment
    Object.assign(values, {
      roiAttachmentId: 0,
      roiAttachmentIdAttachmentName: ''
    });

    setFieldValue(`roiModels.${idx}`,values, true);
  }

  console.log(docModels);

  return (
  <>
  { (verdict === 'approved' && (['SiteInspect','LabelInspect','MandatoryDOC','MandatoryDOC1B','Label1b1','Label1b2','LabelReplacement'].includes(workflow))) &&
  <>
  <div className="row mb-2">
    <FormikSelect size="6" label="Assign Inspector" name="inspectorID" required={true} options={inspectors} placeholder='Select Inspector' />
    <FormikDateSelector size="6" label="Inspection Date" name="inspectionDate" required={true} showShiftSelector={true} dateFormat='LL' />        
  </div>
  </>
  }
  { (verdict === 'approved' && (['DOC','Label5','DOCEdit','DOCSplit','ROIEdit','ROISplit'].includes(workflow))) &&
  <div className="row mb-2">
    <FormikSelect size="6" label="Assign Program Manager" name="pmID" required={true} options={programManagers} placeholder='Select Program Manager' />
  </div>
  }
  { (verdict === 'approved' && (['TriggerTest'].includes(workflow))) &&
  <div className="row mb-2">
    <FormikSelect size="6" label="Assign Quotation Manager" name="qaId" required={true} options={quotationAdmins} placeholder='Select Quotation Admin' />
  </div>
  }
  { (verdict === 'approved' && (['DOC','DOCEdit','DOCSplit'].includes(workflow))) &&
  <>
  <div className="d-flex flex-row justify-content-between align-items-center my-2">
    <div className="fs-6 fw-bold">Upload DOC</div>
    {(! docModels?.length) &&
    <DLMCreateDOCButton appID={appID} />
    }
  </div>
  { (docModels?.length > 0) &&
  <>
  <div className='separator border-gray-200 mb-2'></div>
  <div className="mh-250px overflow-auto pe-4 mb-4">
  {sortBy(docModels, 'cbRef').map((docModel: any, idx: number) => (
  <div key={`setDOCrow_${idx}`} className="row">
    { (docModel?.cbRef) ? (
    <>
    <div className="col-4 mt-1 py-2">
    <DLMGenerateDOCButton index={idx} onGenDOC={setDOCModel} baseData={docModel} />
    </div>
    <FormikFileUpload
      size="8"
      label={`${docModel?.cbRef ? docModel?.cbRef : "DOC Attachment"}`}
      name={`docModels.${idx}.docAttachmentId`}
      acceptFiles="UserFileFormats"
      required={true}
      disabled={(docModel?.docId) ? false : true}
      subTitle={(docModel?.docId) ? "Upload DOC here" : "Generate DOC first."}
      onUpdateFunc={() => {
        setTimeout(() => {
          validateField('docModels');
        }, 100)
      }}
    />
    </>
    ) : (
    <>
    { (idx === 0) && 
    <div className="text-muted fs-7">Create DOC first.</div>
    }
    </>
    )}    
  </div>
  ))
  }
  </div>
  </>
  }
  </>
  }
  { (verdict === 'approved' && (['Label5','Label1b1','ROIEdit','ROISplit','LabelReplacement'].includes(workflow))) &&
  <>
  <div className="d-flex flex-row justify-content-between align-items-center my-2">
    <div className="fs-6 fw-bold">Upload ROI</div>
    {(! roiModels.length) && 
    <DLMCreateROIButton appID={appID} siteID={siteID} />
    }
  </div>
  { (roiModels && roiModels.length > 0) &&
  <>
  <div className='separator border-gray-200 mb-2'></div>
  <div className="mh-250px overflow-auto pe-4">        {
    roiModels.map((roiModel: any, idx: number) => (
    <div key={`setROIrow_${idx}`} className="row mb-4">
      <div className="col-3">
        <DLMGenerateROIButton index={idx} onGenROI={setROIModel} baseData={roiModel} />
      </div>
      <FormikFileUpload
        size="9"
        label={`[${roiModel?.cbRef ? roiModel.cbRef : `${roiModel.series}${roiModel.no}${roiModel?.split ? roiModel?.split :''}`}] ${roiModel.projectName}`}
        name={`roiModels.${idx}.roiAttachmentId`}
        acceptFiles="UserFileFormats"
        required={true}
        disabled={(roiModel?.roiId) ? false : true}
        subTitle={(roiModel?.roiId) ? "Upload ROI here" : "Generate ROI first."}
        onUpdateFunc={() => {
          setTimeout(() => {
            validateField('roiModels');
          }, 100)
        }}
      />
    </div>
    ))
    }
  </div>
  </>
  }
  </>
  }
  <FormikNotesArea required={verdict === 'rejected'} />
  </>
  )
}